import { useEffect, useState } from "react";
import type { Api, LeadAnalytics } from "../api";

export function sourceDisplayLabel(source: string): string {
  const known: Record<string, string> = {
    google: "Google Ads",
    meta: "Meta Ads",
    microsoft: "Microsoft Ads",
    tiktok: "TikTok Ads",
    direct: "Direct",
  };
  return known[source.toLowerCase()] ?? source;
}

export function conversionPercent(leads: number, won: number): string {
  return leads === 0 ? "--" : `${Math.round((won / leads) * 100)}%`;
}

export function downloadLeadsCsv(csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "groundbeam-leads.csv";
  anchor.click();
  URL.revokeObjectURL(url);
}

export function LeadAnalyticsCard({ api }: { api: Api }) {
  const [days, setDays] = useState(30);
  const [analytics, setAnalytics] = useState<LeadAnalytics | null>(null);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setAnalytics(null);
    setError(false);
    api
      .leadAnalytics(days)
      .then((result) => {
        if (!cancelled) setAnalytics(result);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [api, days]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      downloadLeadsCsv(await api.exportLeadsCsv());
    } finally {
      setExporting(false);
    }
  };

  const won = analytics?.totals.byStatus.won ?? 0;
  const maxSource = Math.max(
    0,
    ...(analytics?.sources.map((s) => s.leads) ?? []),
  );
  const maxDaily = Math.max(0, ...(analytics?.daily.map((d) => d.leads) ?? []));
  const statuses = ["new", "contacted", "qualified", "won"];
  if ((analytics?.totals.byStatus.spam ?? 0) > 0) statuses.push("spam");

  return (
    <section className="card lead-analytics-card">
      <div className="card-head lead-analytics-head">
        <div>
          <h2>Leads at a glance</h2>
          <p className="muted">
            Where your enquiries come from and what they&apos;re worth.
          </p>
        </div>
        <div className="lead-analytics-actions">
          <label>
            <span className="visually-hidden">Period</span>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={7}>7 days</option>
              <option value={30}>30 days</option>
              <option value={90}>90 days</option>
            </select>
          </label>
          <button
            className="btn-secondary"
            disabled={exporting}
            onClick={() => void exportCsv()}
            type="button"
          >
            {exporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>

      {!analytics && !error && <p className="muted">Loading...</p>}
      {error && <p className="error">Lead analytics could not be loaded.</p>}
      {analytics && (
        <>
          <div className="lead-analytics-stats">
            {[
              [analytics.totals.leads.toLocaleString(), "Enquiries"],
              [won.toLocaleString(), "Won"],
              [
                `£${Math.round(analytics.totals.wonValue).toLocaleString()}`,
                "Won revenue",
              ],
              [conversionPercent(analytics.totals.leads, won), "Conversion"],
            ].map(([value, label]) => (
              <div className="lead-analytics-stat" key={label}>
                <strong>{value}</strong>
                <span>{label}</span>
              </div>
            ))}
          </div>

          <div className="lead-analytics-section">
            <h3>By source</h3>
            <div className="lead-analytics-sources">
              {analytics.sources.map((source) => (
                <div className="lead-analytics-source" key={source.source}>
                  <span
                    className="lead-analytics-source-label"
                    title={sourceDisplayLabel(source.source)}
                  >
                    {sourceDisplayLabel(source.source)}
                  </span>
                  <span className="lead-analytics-track">
                    <span
                      style={{
                        width: `${maxSource ? (source.leads / maxSource) * 100 : 0}%`,
                      }}
                    />
                  </span>
                  <span className="lead-analytics-count">{source.leads}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="lead-analytics-section">
            <h3>Last {analytics.days} days</h3>
            {analytics.daily.length === 0 ? (
              <p className="muted small">No enquiries in this period yet.</p>
            ) : (
              <div className="lead-analytics-daily">
                {analytics.daily.map((day) => (
                  <span
                    key={day.day}
                    title={`${day.day} -- ${day.leads} enquiries`}
                    style={{
                      height: `${maxDaily ? (day.leads / maxDaily) * 48 : 0}px`,
                    }}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="lead-analytics-funnel">
            {statuses.map((status, index) => (
              <span key={status}>
                {index > 0 && (
                  <span className="lead-analytics-separator"> · </span>
                )}
                {status} {analytics.totals.byStatus[status] ?? 0}
              </span>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
